import { createDebug } from '@/utils/debug'

const log = createDebug('ppt:pointer-repair')

// AHA-47957: repair a click whose RELEASE was delivered in the wrong coordinate space.
//
// In PowerPoint desktop Presenter View the add-in lives in the slideshow window (on the
// secondary display), and the Presenter View pane on the primary display shows a SCALED MIRROR
// of that same web view — same frame, same JS context, confirmed by FRAME_ID being identical for
// input arriving from either surface. PowerPoint forwards input from the mirror into the frame
// but only translates the PRESS. The release is passed through untranslated, still carrying the
// Presenter View window's own coordinates, which land outside the frame entirely.
//
// Measured on the failing setup, one gesture with the mouse held still:
//
//   pointerdown  client 654,597   screen -940,895   target div.start-game-button-content
//   pointerup    client 1715,467  screen  651,699   target html, hit-test null
//   click        client 1714,466  screen  651,699   target html, hit-test null
//
// A DOM click fires on the nearest common ancestor of the mousedown and mouseup targets, so a
// relocated release retargets the whole click to <html> and the button's own listener never runs.
// Nothing of ours executes — this is not a guard of ours rejecting the press, the press simply
// never becomes a click on the element it landed on.
//
// Across two widely separated presses the relocation is one affine transform,
// release = 0.64 x press + (1293, 85), and the same 0.64 falls out of the screen coordinates
// independently; 0.64 is the Presenter View pane's mirror scale. Clicking the same button on the
// audience-facing slideshow window works perfectly in the same frame, which is what localises the
// fault to the mirror's input forwarding rather than to anything about the add-in.
//
// Every click target in the add-in is affected, not just the quiz Start button, which is why this
// repairs the gesture centrally instead of rewiring individual controls.
//
// KNOWN LIMITATION — hover menus. UI that reacts to the RELEASE cannot be saved by retargeting the
// click, because it has already changed by the time a click exists. The effects menu is the worked
// example: it opens on hover and closes on `@mouseleave` (EffectMenuToggle.vue), so the relocated
// mouseup landing outside it makes the browser recompute hover, fire mouseleave, and unmount the
// menu about 7ms before the click arrives — at which point the pressed item is detached and the
// repair declines rather than resurrect a handler on a destroyed component. Fixing that needs a
// change in the menu itself (do not close on mouseleave while a press is in flight), not here.
//
// WHY NOT setPointerCapture. Capture overrides hit-testing, so pointerup would reach the element
// despite its coordinates — but it only fixes pointer events: `click` is still computed from the
// mouse pair and would still land on <html>, so anything bound to @click (which is nearly
// everything) stays broken. Retargeting the click itself fixes the whole surface at one point.
//
// It was also TRIED as a complement to the click repair, to stop a relocated release moving hover
// state and closing hover menus before their click arrives (see the effects-menu limitation at the
// bottom of this comment). It was reverted: on the affected host the capture is never released and
// the add-in dies after a few clicks. Every later pointer event is retargeted to the stale capture
// element, and because boundary events fire only at the capture target the open menu never gets
// its mouseleave and freezes on screen — both observed on hardware.
//
// The likely reason implicit release never fires is that the relocated pointerup does not carry
// the pointerId the press was captured with, so nothing ever matches it. Anyone reattempting this
// needs to log pointerId on both ends FIRST and prove they pair, and needs an explicit release
// plus a watchdog — capture that leaks in a live presentation is far worse than the bug it fixes.
//
// WHY NOT act on the press. Firing on pointerdown works — press coordinates are reliable — but it
// changes every control in the add-in to activate on press, losing drag-off-to-cancel, and does
// so on healthy setups too.

// The trigger, and the reason this is safe to run unconditionally in a PPT frame: a click whose
// coordinates hit-test to NOTHING. elementFromPoint returns null only for a point outside the
// viewport, and a real user click is by definition inside it — so a null hit-test is positive
// proof the coordinates are bogus, not a heuristic about where the pointer "probably" went. On a
// healthy display the condition is never true and this module does nothing at all.
const isOutsideViewport = (event) => {
  if (typeof event.clientX !== 'number' || typeof event.clientY !== 'number') return false
  // Fails safe if the host has no elementFromPoint: no evidence the coordinates are bogus means
  // no repair, which leaves behaviour exactly as it is today.
  if (typeof document.elementFromPoint !== 'function') return false
  return document.elementFromPoint(event.clientX, event.clientY) === null
}

// Marks a click this module dispatched, so the capture listener lets its own synthetic event
// through instead of treating it as another broken one and recursing.
const REPAIRED = '__ahaPptRepairedClick'

// The store is resolved LAZILY rather than by a static import. This module is pulled in from the
// Office boot path, and the store's own module graph reaches back into it
// (vuex/modules -> presentation/index -> editor.module -> services/office), so a static import
// would close that cycle and leave this module half-initialised at boot — the same hazard debug.js
// and frame-badge.js document, handled the same way. By the time a click can fire, every module is
// evaluated. Fails safe: no store means no repair, i.e. today's behaviour.
let storeRef = null
const getStore = () => {
  if (!storeRef) {
    try {
      // eslint-disable-next-line global-require
      storeRef = require('@/vuex/store').default
    } catch (error) {
      storeRef = null
    }
  }
  return storeRef
}

// A press is only worth remembering if it landed on something that is not the fallback target a
// relocated event itself produces — otherwise a genuine click on the page background would be
// "repaired" onto the background.
const isRepairableTarget = (target) => {
  return Boolean(
    target
    && target.nodeType === Node.ELEMENT_NODE
    && target !== document.documentElement
    && target !== document.body,
  )
}

// Bounded so a press that never produced a click (a drag out of the frame, a press swallowed by
// the host) cannot be paired with an unrelated click minutes later.
const PRESS_MAX_AGE_MS = 2000

let lastPress = null

const handlePress = (event) => {
  if (!isRepairableTarget(event.target)) {
    lastPress = null
    return
  }
  // clientX/Y are kept because the press coordinates are the CORRECT ones: the synthetic click
  // carries them, so a handler that reads position off the event gets the place the user actually
  // pressed rather than the zeroes a bare MouseEvent would give it.
  lastPress = {
    target: event.target,
    at: Date.now(),
    clientX: event.clientX,
    clientY: event.clientY,
    screenX: event.screenX,
    screenY: event.screenY,
  }
}

const handleClick = (event) => {
  // Our own repair, on its way to the element's listeners.
  if (event[REPAIRED]) return
  // Read per event, not once at install, so turning the flag off is a live kill switch during a
  // presentation rather than something that needs a reload to take effect.
  const store = getStore()
  if (!store || !store.getters['featureToggles/canUsePptPointerGestureRepair']) return
  // detail === 0 is a click synthesised from the keyboard (Enter/Space on a focused control).
  // It has no meaningful coordinates and no release to relocate, so it is never ours to repair.
  if (event.detail === 0) return

  // Consumed on every click that gets this far, not only the repaired ones. A press belongs to
  // exactly one click; leaving it set after a healthy click would let a later coordinate-less
  // click be paired with a stale target up to PRESS_MAX_AGE_MS afterwards.
  const press = lastPress
  lastPress = null

  if (!isOutsideViewport(event)) return
  if (!press) return
  if (Date.now() - press.at > PRESS_MAX_AGE_MS) return
  // The element may have been unmounted between press and click — a slide change, a modal close.
  // Dispatching at a detached node would do nothing and could resurrect a stale handler.
  if (!press.target.isConnected) return

  log('relocated click repaired | click at', event.clientX, event.clientY,
    '(hit-tests to nothing) -> press target', press.target.tagName)

  // Kill the bogus event before anything sees it. In this state the click is a mistranslation,
  // never a real click on <html> — leaving it to propagate would fire every close-on-click-outside
  // handler in the app on a click the user made INSIDE a control.
  event.preventDefault()
  event.stopImmediatePropagation()

  const repaired = new MouseEvent('click', {
    bubbles: true,
    cancelable: true,
    view: window,
    detail: 1,
    clientX: press.clientX,
    clientY: press.clientY,
    screenX: press.screenX,
    screenY: press.screenY,
  })
  repaired[REPAIRED] = true
  press.target.dispatchEvent(repaired)
}

let installed = false

// Both listeners are on WINDOW in the capture phase, which is the first node in the capture path
// — earlier than any capture listener on document, and earlier still than target and bubble.
//
// That ordering is what keeps the repair from double-firing anything. stopImmediatePropagation
// below cancels the bogus click for every listener that has not already run, so a listener
// downstream of this one sees ONLY the repaired click; the app's own capture-phase document click
// listeners (OtherSettings, SettingMoreActionsMenu, ConvertToLessonNudge, MyFilesCoachmark) are
// all downstream of window and so are covered. The one thing that would still see both events is
// another WINDOW capture listener registered before this module installs — there are none in the
// app, and this module installs at Office boot, before any component mounts.
//
// The press listener is here for a different reason: so a stopPropagation somewhere in the app
// cannot hide the press that the repair depends on.
export const installPptPointerGestureRepair = () => {
  if (typeof document === 'undefined' || installed) return
  installed = true
  window.addEventListener('pointerdown', handlePress, true)
  window.addEventListener('click', handleClick, true)
  log('pointer gesture repair installed')
}

export const removePptPointerGestureRepair = () => {
  if (typeof document === 'undefined' || !installed) return
  installed = false
  window.removeEventListener('pointerdown', handlePress, true)
  window.removeEventListener('click', handleClick, true)
  lastPress = null
}

export default installPptPointerGestureRepair
